import React, { useCallback, useContext, useMemo, useState } from 'react'
import { AuthContext } from '../../context/auth/auth-context'
import axios from 'axios'
import { formApiGatewayApiUrl } from '../../utils/api/servers'
import dayjs, { type Dayjs } from 'dayjs'
import { type DateRange } from '@mui/x-date-pickers-pro'
import { DateTimeRangePicker } from '@mui/x-date-pickers-pro/DateTimeRangePicker'
import { Typography, Stack, TextField, Grid, Card, CardContent, Button, type AlertColor } from '@mui/material'
import { DataGridPro, type GridAutosizeOptions, type GridColDef, useGridApiRef } from '@mui/x-data-grid-pro'
import { type GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro'
import { type GridRowModelUpdate } from '@mui/x-data-grid/models/gridRows'
import StackColumnLayout from '../../layouts/stack-column-layout'
import FilterExportGridToolbar from '../../components/data-grid/filter-export-grid-toolbar'
import { formatToLocalMilliTimestampFromUTCNumber } from '../../utils/time-utils'
import { formAlertErrorMessage, formAlertSuccessMessage, isEmptyString } from '../../utils/string-utils'
import ToastWithMessage from '../../components/toast/toast-with-message'
import MiniLoader from '../../components/loader/mini-loader'
import { useEffectTimeout } from '../../hooks/use-effect-timeout'

const subTypeMap = {
  1: 'Buy',
  2: 'Sell',
  3: 'Open long',
  4: 'Open short',
  5: 'Close long',
  6: 'Close short',
  100: 'Partial liquidation close long',
  101: 'Partial liquidation close short',
  102: 'Partial liquidation buy',
  103: 'Partial liquidation sell',
  104: 'Liquidation long',
  105: 'Liquidation short',
  106: 'Liquidation buy',
  107: 'Liquidation sell',
  110: 'Liquidation transfer in',
  111: 'Liquidation transfer out',
  118: 'System token conversion transfer in',
  119: 'System token conversion transfer out',
  125: 'ADL close long',
  126: 'ADL close short',
  127: 'ADL buy',
  128: 'ADL sell',
  212: 'Auto borrow of quick margin',
  213: 'Auto repay of quick margin',
  204: 'block trade buy',
  205: 'block trade sell',
  206: 'block trade open long',
  207: 'block trade open short',
  208: 'block trade close open',
  209: 'block trade close short',
  270: 'Spread trading buy',
  271: 'Spread trading sell',
  272: 'Spread trading open long',
  273: 'Spread trading open short',
  274: 'Spread trading close long',
  275: 'Spread trading close short',
}

const columns: GridColDef[] = [
  {
    field: 'ts',
    headerName: 'Insert Time',
    width: 220,
    valueFormatter: value => formatToLocalMilliTimestampFromUTCNumber(value)
  },
  {
    field: 'fillTime',
    headerName: 'Fill Time',
    width: 220,
    valueFormatter: value => formatToLocalMilliTimestampFromUTCNumber(value)
  },
  {
    field: 'tradeId',
    headerName: 'Trade ID',
    width: 120,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    }
  },
  {
    field: 'instType',
    headerName: 'Inst. Type',
    width: 80,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    }
  },
  {
    field: 'instId',
    headerName: 'Instrument',
    width: 160,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    }
  },
  {
    field: 'side',
    headerName: 'Side',
    width: 60,
    valueFormatter: value => String(value).toUpperCase()
  },
  {
    field: 'subType',
    headerName: 'Transaction Type',
    width: 125,
    valueGetter: value => subTypeMap[value]
  },
  {
    field: 'execType',
    headerName: 'Exec. Type',
    width: 80,
    valueGetter: value => value === 'T' ? 'Taker' : (value === 'M' ? 'Maker' : '?')
  },
  {
    field: 'posSide',
    headerName: 'Pos. Side',
    width: 80,
    valueFormatter: value => String(value).toUpperCase()
  },
  {
    field: 'tag',
    headerName: 'Tag',
    width: 50,
  },
  {
    field: 'feeCcy',
    headerName: 'Fee / Rebate Ccy',
    width: 125
  },
  {
    field: 'fee',
    headerName: 'Fee / Rebate',
    width: 100
  },
  {
    field: 'fillPx',
    headerName: 'Filled Px',
    width: 80
  },
  {
    field: 'fillSz',
    headerName: 'Filled Qty',
    width: 80
  },
  {
    field: 'fillIdxPx',
    headerName: 'Index Px',
    width: 80
  },
  {
    field: 'fillMarkPx',
    headerName: 'Mark Px',
    width: 80
  },
  {
    field: 'ordId',
    headerName: 'Order ID',
    width: 120,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    }
  },
  {
    field: 'clOrdId',
    headerName: 'Client Order ID',
    width: 120,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    }
  },
  {
    field: 'billId',
    headerName: 'Bill ID',
    width: 120,
    sortComparator: (v1, v2) => {
      return v1.localeCompare(v2, undefined, { numeric: true })
    }
  },
]

const autosizeOptions: GridAutosizeOptions = {
  columns: Array.from(columns, item => item?.field),
  includeHeaders: true,
  includeOutliers: true,
  expand: true,
}

const initialState: GridInitialStatePro = {
  pinnedColumns: { },
  sorting: {
    sortModel: [
      { field: 'ts', sort: 'desc' },
    ],
  },
}

const ReconciliationView: React.FC = () => {
  const minDate = useMemo(() => dayjs().subtract(3, 'day'), [])
  const maxDate = useMemo(() => dayjs(), [])

  const [missingTradeRows, setMissingTradeRows] = useState<GridRowModelUpdate[]>([])
  const [matchingTradeRows, setMatchingTradeRows] = useState<GridRowModelUpdate[]>([])

  const [selectedAccount, setSelectedAccount] = useState<string>('')
  const [selectedDateTimeRange, setSelectedDateTimeRange] = useState<DateRange<Dayjs>>([
    dayjs().subtract(1, 'hour'), dayjs()
  ])

  const gridApiRef1 = useGridApiRef()
  const gridApiRef2 = useGridApiRef()
  const [isProcessingRequest, setIsProcessingRequest] = useState<boolean>(false)

  const { accessToken } = useContext(AuthContext)

  const handleSubmit = useCallback(() => {
    setIsProcessingRequest(true)

    if (isEmptyString(selectedAccount)) {
      console.debug('[Reconciliation] Invalid account - account cannot be empty')
      displayToastWithMessage('Please provide an account and try again', 'warning')
      setIsProcessingRequest(false)
      return
    }

    const startTime = selectedDateTimeRange[0]
    const endTime = selectedDateTimeRange[1]

    if (startTime == null || endTime == null) {
      console.debug('[Reconciliation] Invalid date - start and end times cannot be null')
      displayToastWithMessage('Please provide a valid time range and try again', 'warning')
      setIsProcessingRequest(false)
      return
    }

    if (endTime.diff(startTime) < 0) {
      console.debug('[Reconciliation] Invalid time range - start time must be before end time')
      displayToastWithMessage('Please provide a valid time range and try again', 'warning')
      setIsProcessingRequest(false)
    }

    if (endTime.diff(startTime) > 21_600_000) { // 6 hours
      console.debug('[Reconciliation] Maximum time range of six hours exceeded')
      displayToastWithMessage('Please provide a time range of at most 6 hours and try again', 'warning')
      setIsProcessingRequest(false)
    }

    const message = {
      timestamp: new Date().toISOString(),
      account: selectedAccount,
      begin: startTime.unix() * 1000,
      end: endTime.unix() * 1000
    }

    console.debug('[Reconciliation] Sending reconciliation request:')
    console.debug(message)

    axios
      .post(formApiGatewayApiUrl('strategy-management/reconciliation'), message, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(response => {
        console.debug(response)
        const data: { matching: GridRowModelUpdate[], missing: GridRowModelUpdate[] } = response.data.data
        displayToastWithMessage(formAlertSuccessMessage(response.status, response.statusText, `${data.missing.length} Missing, ${data.matching.length} Matching`), 'success')
        setMissingTradeRows(data.missing)
        setMatchingTradeRows(data.matching)
      })
      .catch(error => {
        console.error(error)
        displayToastWithMessage(formAlertErrorMessage(error), 'error')
        setMissingTradeRows([])
        setMatchingTradeRows([])
      })
      .finally(() => {
        setIsProcessingRequest(false)
      })
  }, [selectedAccount, selectedDateTimeRange, accessToken])

  const resizeColumns = useCallback(() => {
    // Ignored IIFE to resize all columns
    void (async function () {
      await gridApiRef1.current.autosizeColumns(autosizeOptions)
      await gridApiRef2.current.autosizeColumns(autosizeOptions)
    })()
  }, [gridApiRef1, gridApiRef2, autosizeOptions])
  useEffectTimeout(resizeColumns, 200, [missingTradeRows, matchingTradeRows])

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')
  const displayToastWithMessage = useCallback((message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }, [])

  return (
    <StackColumnLayout>
      <Grid container spacing={4} padding={4} marginTop={0}>
        <Grid item xs={12}>
          <Typography variant='subtitle2' gutterBottom>
            Provide the account and time range for trade reconciliation:
          </Typography>
          <Card variant='outlined'>
            <CardContent>
              <Grid container padding={2} spacing={2} alignItems='center'>
                <Grid item xs={12}>
                  <Typography variant='subtitle2' gutterBottom>
                    Note: Maximum time range is 6 hours looking back over the past 3 days only.
                  </Typography>
                </Grid>

                <Grid item xs={12} md='auto'>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    variant='outlined'
                    fullWidth
                    size='small'
                    label='Account'
                    error={isEmptyString(selectedAccount)}
                    value={selectedAccount}
                    onChange={event => {
                      setSelectedAccount(event.target.value)
                    }}
                  />
                </Grid>

                <Grid item xs={12} md='auto'>
                  <DateTimeRangePicker
                    minDate={minDate}
                    maxDate={maxDate}
                    value={selectedDateTimeRange}
                    onChange={value => { setSelectedDateTimeRange(value) }}
                  />
                </Grid>

                <Grid item xs={12} md='auto'>
                  <Stack direction='row' gap={2} alignItems='center'>
                    <Button
                      variant='contained'
                      fullWidth
                      size='small'
                      disabled={isProcessingRequest}
                      onClick={handleSubmit}
                    >
                      Reconcile Trades
                    </Button>
                    {isProcessingRequest && <MiniLoader/>}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Stack gap={2}>
            <Stack direction='row' gap={1}>
              <Typography color='secondary'>Missing Trades:</Typography>
            </Stack>
            <DataGridPro
              apiRef={gridApiRef1}
              rows={missingTradeRows}
              columns={columns}
              getRowId={row => row.tradeId}
              autosizeOnMount={true}
              autosizeOptions={autosizeOptions}
              initialState={initialState}
              autoHeight={true}
              columnHeaderHeight={40}
              rowHeight={20}
              slots={{
                toolbar: FilterExportGridToolbar,
              }}
              slotProps={
                { toolbar: { csvOptions: { fileName: 'missing_trades', allColumns: true } } }
              }
            />

            <Stack direction='row' gap={1}>
              <Typography color='secondary'>Matching Trades:</Typography>
            </Stack>
            <DataGridPro
              apiRef={gridApiRef2}
              rows={matchingTradeRows}
              columns={columns}
              getRowId={row => row.tradeId}
              autosizeOnMount={true}
              autosizeOptions={autosizeOptions}
              initialState={initialState}
              autoHeight={true}
              columnHeaderHeight={40}
              rowHeight={20}
              slots={{
                toolbar: FilterExportGridToolbar,
              }}
              slotProps={
                { toolbar: { csvOptions: { fileName: 'matching_trades', allColumns: true } } }
              }
            />
          </Stack>
        </Grid>
      </Grid>

      <ToastWithMessage toastOpen={toastOpen} setToastOpen={setToastOpen} message={toastMessage} severity={toastSeverity}/>
    </StackColumnLayout>
  )
}

export default ReconciliationView
